import { map } from 'lodash';
import { ChangeEvent, FC } from 'react';
import DatePicker from 'react-datepicker';

interface TextFieldProps {
  name: string;
  type?: string;
  label?: string;
  value?: string | number;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  error?: string;
  required?: boolean;
  className?: string;
  isTouched?: boolean;
  disabled?: boolean;
}

interface TextAreaProps {
  rows?: number;
  name: string;
  label?: string;
  value?: string;
  placeholder?: string;
  error?: string;
  required?: boolean;
  className?: string;
  isTouched?: boolean;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
}

interface SelectFieldProps {
  name: string;
  label?: string;
  value?: string | number;
  onChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (event: ChangeEvent<HTMLSelectElement>) => void;
  error?: string;
  required?: boolean;
  className?: string;
  isTouched?: boolean;
  items: { title: string; value: string }[];
  disabled?: boolean;
}

interface AppDatePickerProps {
  name: string;
  label?: string;
  value?: Date;
  onChange: (date: Date | null) => void;
  error?: string;
  required?: boolean;
  className?: string;
  isTouched?: boolean;
  disabled?: boolean;
  maxDate?: Date;
  startDate?: Date;
  placeholder?: string;
  showMonthDropdown: boolean;
  showYearDropdown: boolean;
  yearDropdownItemNumber?: number;
  scrollableYearDropdown?: boolean;
  minDate?: Date;
}

export const TextField: FC<TextFieldProps> = ({
  name,
  type = 'text',
  label,
  onChange,
  value,
  required,
  className = '',
  placeholder,
  error,
  onBlur,
  isTouched,
  disabled,
}) => {
  return (
    <div className="block w-full">
      <label
        htmlFor={'#' + name}
        className="block h-full mb-1 sm:text-[14px] text-[#666]"
      >
        {label}
      </label>
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur && onBlur}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className={`
          ${error && isTouched ? 'border-red-600' : ''} ${className} sm:h-[42px]
          border border-[#ccc] rounded-xl px-4 py-3 w-full`}
      />
      {error && isTouched && (
        <div className="text-[12px] text-red-600 pl-2">{error}</div>
      )}
    </div>
  );
};

export const TextArea: FC<TextAreaProps> = ({
  name,
  label,
  onChange,
  value,
  required,
  className = '',
  placeholder,
  error,
  onBlur,
  isTouched,
  rows = 4,
}) => {
  return (
    <div className="block w-full">
      <label
        htmlFor={'#' + name}
        className="block h-full mb-1 sm:text-[14px] text-[#666]"
      >
        {label}
      </label>
      <textarea
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur && onBlur}
        placeholder={placeholder}
        required={required}
        className={`
          ${error && isTouched ? 'border-red-600' : ''} ${className}
          border border-[#ccc] rounded-xl px-4 py-3 w-full`}
        rows={rows}
      />
      {error && isTouched && (
        <div className="text-[12px] text-red-600 pl-2">{error}</div>
      )}
    </div>
  );
};

export const SelectField: FC<SelectFieldProps> = ({
  name,
  label,
  onChange,
  value,
  required,
  className = '',
  error,
  onBlur,
  isTouched,
  items,
  disabled,
}) => {
  return (
    <div className="block w-full">
      <label
        htmlFor={'#' + name}
        className="block h-full mb-1 sm:text-[14px] text-[#666]"
      >
        {label}
      </label>
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur && onBlur}
        required={required}
        className={`
          ${error && isTouched ? 'border-red-600' : ''} ${className} sm:h-[42px]
          border border-[#ccc] rounded-xl px-4 w-full disabled:bg-[rgba(239,239,239,0.4)]`}
        disabled={disabled}
      >
        {items.length > 0 &&
          map(items, (item) => (
            <option
              key={item.value}
              value={item.value}
              selected={item.value === value}
            >
              {item.title}
            </option>
          ))}
      </select>
      {error && isTouched && (
        <div className="text-[12px] text-red-600 pl-2">{error}</div>
      )}
    </div>
  );
};

export const AppDatePicker: FC<AppDatePickerProps> = ({
  name,
  label,
  onChange,
  value,
  required,
  className = '',
  error,
  isTouched,
  disabled,
  showMonthDropdown,
  showYearDropdown,
  yearDropdownItemNumber = 100,
  scrollableYearDropdown = true,
  placeholder,
  maxDate,
  startDate,
  minDate
}) => {
  return (
    <div className="block w-full">
      <label
        htmlFor={'#' + name}
        className="block h-full mb-1 sm:text-[14px] text-[#666]"
      >
        {label}
      </label>

      <DatePicker
        maxDate={maxDate}
        startDate={startDate}
        onChange={(date) => onChange && onChange(date)}
        minDate={minDate}
        selected={value as Date}
        placeholderText={placeholder}
        showMonthDropdown={showMonthDropdown}
        showYearDropdown={showYearDropdown}
        yearDropdownItemNumber={yearDropdownItemNumber}
        scrollableYearDropdown={scrollableYearDropdown}
        disabled={disabled}
        required={required}
        name={name}
        id={name}
        // className="h-[40px] w-full rounded-lg focus:outline-none pl-2 bg-transparent dark:text-[#bdbdbd]"
        className={`
          ${error && isTouched ? 'border-red-600' : ''} ${className} sm:h-[42px]
          border border-[#ccc] rounded-xl px-4 py-3 w-full block`}
        wrapperClassName='app-date-picker'
      />
      {error && isTouched && (
        <div className="text-[12px] text-red-600 pl-2">{error}</div>
      )}
    </div>
  );
};
